import React, { useCallback, useState } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import { isCasinoGamePage } from 'utils';

import {
  AccountManager,
  btag,
  getActiveForms,
  NotificationManager,
  pushToDataLayer,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
} from '@atlas/core';
import { onSignInSubmit, useActiveForm } from '@atlas/react-forms';
import { COMPONENT_TYPES, staticRoutes } from '@atlas/routing';
import { DefaultPopup, HeaderTitle, Icon, Label } from '@atlas/ui';

import {
  ACCOUNT_STATUSES,
  BTAG_STORAGE_KEY,
  CXFROM_STORAGE_KEY,
  OAUTH_FLOW_TYPES,
  SIGN_IN_POPUP_STORAGE_KEY,
} from 'common/constants';
import FormWithCaptcha from 'common/Form/FormWithCaptcha/FormWithCaptcha';
import { displayMultiPopup, getMultiPopupConfig } from 'common/Popup/MultiPopup/MultiPopupManager';
import { getTerminalLoginConfig } from 'common/Popup/TerminalLoginPopup/TerminalLoginPopupManager';
import StaticBlock from 'common/StaticBlock/StaticBlock';
import { camelCase } from 'common/utils/camelCase';
import Logo from 'common/Widgets/Logo/Logo';
import ShortFooter from 'components/Footer/ShortFooter';
import { defaultRoutes } from 'components/MainRouter/constants';
import { useHidePopupSignUpDelay } from 'hooks/useHidePopupSignUpDelay';
import { useSetNextShowPopupSignUpDelay } from 'hooks/useSetNextShowPopupSignUpDelay';
import SocialProviders from 'src/components/Auth/SocialProviders';
import useSignInRedirects from 'src/components/Auth/useSignInRedirects';
import { useSignupPopup } from 'src/components/Auth/useSignUpPopup';
import useSocialProviders from 'src/components/Auth/useSocialProviders';
import { customSignupFormRules } from 'src/helpers/customFormRules';

import { resolveSignInAutoComplete } from './formAutocomplete';

const getSocialRedirectPath = ({ cxfromPath, locationState }) => {
  const stateRedirectPath = locationState?.data?.redirectPath;
  const storagePath = cxfromPath !== staticRoutes.signup ? cxfromPath : null;
  return stateRedirectPath ?? storagePath ?? null;
};

const get = (object, path) => {
  if (typeof path === 'string') {
    // eslint-disable-next-line no-param-reassign
    path = path.split('.').filter(key => key.length);
  }
  return path.reduce((dive, key) => dive && dive[key], object);
};

const SignIn = () => {
  const [isSubmittedForm, setSubmittedForm] = useState(false);
  const { redirectAfterSignIn } = useSignInRedirects();
  const history = useHistory();
  const { pathname, search, state } = useLocation();
  const signInPopup = SiteConfigManager.getConfig(
    'config.config.mobile.signin_in_popup_format',
    false,
  );

  const applicationState = useApplicationState();
  const { account, authenticated, resolved } = applicationState;
  const isSignupPopupOpen = search.includes('cmd=signup');

  const cxfromPath = StorageManager.getItem(CXFROM_STORAGE_KEY);
  const socialRedirectPath = getSocialRedirectPath({ cxfromPath, locationState: state });
  const showRegistration = SiteConfigManager.getConfig('config.config.enable_registration');
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);

  useHidePopupSignUpDelay();
  useSetNextShowPopupSignUpDelay();

  const { activeStep, activeType, setActiveType, submit, types } = useActiveForm(
    'CLIENT_LOGIN',
    onSignInSubmit(AccountManager.loginWithForm.bind(AccountManager)),
    getActiveForms,
  );
  const initialCaptchaKey = activeType?.optionalParams.captchaKey;
  const initialCaptchaRequired = activeType?.optionalParams.captchaRequired;

  const formFields = activeType?.steps[activeStep]?.fields;
  const realFormFields = React.useMemo(() => {
    if (formFields) {
      return formFields.map(field => ({
        ...field,
        ...customSignupFormRules(field),
        autocomplete: resolveSignInAutoComplete(field),
      }));
    }
    return formFields;
  }, [formFields]);

  React.useEffect(() => {
    const params = new URLSearchParams(search);
    const path = params.get('path');

    const newActiveType = types.find(({ title }) => path === camelCase(title));

    if (newActiveType === activeType) {
      return;
    }

    if (newActiveType) {
      setActiveType(newActiveType);
    } else if (activeType) {
      params.set('path', camelCase(activeType.title));

      history.replace(`${pathname}?${params.toString()}`, state);
    }
  }, [history, pathname, activeType, types, search, setActiveType, state]);

  const onBackButtonClick = React.useCallback(() => {
    StorageManager.removeItem(CXFROM_STORAGE_KEY);

    const pathname = state?.prevLocation?.pathname || '';

    if (isCasinoGamePage(pathname, [casinoPath, casinoLivePath])) {
      return history.push(`/${pathname.split('/')[1]}`);
    }

    return history.length === 1 ? history.push('/') : history.goBack();
  }, [casinoLivePath, casinoPath, history, state?.prevLocation?.pathname]);

  const { openSignupPopup } = useSignupPopup();

  const onCloseButtonClick = useCallback(() => {
    history.push('/');
  }, [history]);

  const signUpPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);

  const submitHandler = React.useCallback(
    data => {
      setSubmittedForm(true);
      const params = qs.parse(search);
      const notificationsQuery = [];

      const terminalLoginPopupConfig = getTerminalLoginConfig();
      if (terminalLoginPopupConfig) {
        notificationsQuery.push(terminalLoginPopupConfig);
      }

      if (SiteConfigManager.getConfig('config.config.responsible_gaming.show_popup')) {
        notificationsQuery.push({
          data: {},
          type: 'responsibleGaming',
        });
      }

      const signinMultiPopupConfig = getMultiPopupConfig('sign_in');
      if (signinMultiPopupConfig && (!params || !params.casinoGameId)) {
        notificationsQuery.push({
          customDisplay: () => displayMultiPopup('sign_in', SIGN_IN_POPUP_STORAGE_KEY),
          data: signinMultiPopupConfig,
          id: 'multiPopup',
          type: 'multiPopup',
        });
      }

      const callPopup = config =>
        config.customDisplay
          ? config.customDisplay()
          : setTimeout(() => NotificationManager.display(config), config.delay || 0);

      notificationsQuery.map((_, index) => {
        if (index !== notificationsQuery.length - 1 && notificationsQuery[index + 1]) {
          notificationsQuery[index].data.onClose = () => callPopup(notificationsQuery[index + 1]);
        }
      });

      const showFirstPopup = () => {
        if (notificationsQuery[0]) callPopup(notificationsQuery[0]);

        NotificationManager.notify({
          message: 'welcome_back',
          status: 'success',
          type: 'notification',
        });
      };

      if (cxfromPath) {
        history.replace(cxfromPath || '/');

        StorageManager.removeItem(CXFROM_STORAGE_KEY);
        StorageManager.removeItem(BTAG_STORAGE_KEY);
      } else {
        redirectAfterSignIn();
      }

      if (get(data, 'is2FaEnabled')) {
        NotificationManager.display({
          data: {
            ...data,
            onClose: showFirstPopup,
          },
          type: 'twoFactorLogin',
        });
        return;
      }

      showFirstPopup();
    },
    [cxfromPath, search, history, redirectAfterSignIn],
  );

  const handleForgotPasswordClick = useCallback(() => {
    pushToDataLayer('forgot_password_click', btag);
  }, []);

  const socialProviders = useSocialProviders(activeType);

  const isInMultistepRegistrationFlow =
    isSignupPopupOpen && account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  if (!isSubmittedForm && resolved && authenticated && !isInMultistepRegistrationFlow) {
    return <Redirect to="/" />;
  }

  const renderForm = (
    <>
      {activeType && (
        <FormWithCaptcha
          action={submit}
          autoComplete
          buttonId="signIn"
          buttonText="Sign in"
          form={{
            fields: realFormFields,
            number: activeStep,
          }}
          formType={activeType}
          id={activeType.id}
          initialCaptchaKey={initialCaptchaKey}
          initialCaptchaRequired={initialCaptchaRequired}
          onSubmit={submitHandler}
          validateRequestHeaders={{
            'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          }}
        />
      )}
      {activeStep === 0 && (
        <SocialProviders
          flowType={OAUTH_FLOW_TYPES.SIGNIN}
          items={socialProviders}
          redirectPath={socialRedirectPath}
        />
      )}
      {showRegistration && (
        <div className="links">
          <Label className="title-300-text" message="not_member" />
          &nbsp;
          {signUpPopup ? (
            <button
              className="title-bold-text link signup-popup-link"
              onClick={() => {
                StorageManager.removeItem(CXFROM_STORAGE_KEY);
                openSignupPopup();
              }}
            >
              <Label message="registration" />
            </button>
          ) : (
            <Link onClick={() => StorageManager.removeItem(CXFROM_STORAGE_KEY)} to="signup">
              <Label className="title-bold-text link" message="Registration" />
            </Link>
          )}
        </div>
      )}
      <Link onClick={handleForgotPasswordClick} to={defaultRoutes.forgotPassword}>
        <Label className="title-bold-text link" message="forgot_password" />
      </Link>
    </>
  );

  if (signInPopup) {
    return (
      <DefaultPopup
        className="signup-popup-wrapper"
        headerIcon={<Logo />}
        headerIconPosition="left"
        onClose={onCloseButtonClick}
      >
        <StaticBlock
          className="signup-popup-static-block"
          options={{ source: 'mobile_signin_banner' }}
        />
        <HeaderTitle title="signin_continue" />
        <div className="popup__content">
          <div className="content">{renderForm}</div>
        </div>
      </DefaultPopup>
    );
  }

  return (
    <div className="auth sign-in">
      <div className="header">
        <button className="top-button back-button" onClick={onBackButtonClick}>
          <Icon className="icon-back" name="back" />
        </button>
        <Label message="sign_in" />
      </div>
      <div className="content">{renderForm}</div>
      <ShortFooter isAlwaysRendered />
    </div>
  );
};

export default SignIn;
